import { useState } from "react";
import {
  Box,
  Circle,
  CircleDot,
  CircleDotDashedIcon,
  Computer,
  Grid3X3,
  Hexagon,
  Moon,
  Square,
  Sun,
} from "lucide-react";
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useTheme } from "@/components/theme-provider";
import { applyRadius } from "@/hooks/use-radius-preset";

/**
 * The "Edit Appearence" submenu rows (nav-user dropdown): one segmented
 * icon-tabs control per setting. Keys/values mirror the standalone toggles in
 * components/ui/{mode,radius,background,color}-toggle.tsx, which still render
 * on NotFound — keep them in sync. The auth pages reuse this component
 * (AuthLayout's AppearanceMenu). This menu is the only appearance editor —
 * Settings has no Appearance page since 2026-10-27.
 */

const VITE_ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT;

const THEME_OPTIONS = [
  { key: "light", name: "Light", icon: <Sun className="size-3.5" /> },
  { key: "dark", name: "Dark", icon: <Moon className="size-3.5" /> },
  { key: "system", name: "System", icon: <Computer className="size-3.5" /> },
];

const RADIUS_OPTIONS = [
  { key: "square", name: "Square", icon: <Square className="size-3.5" /> },
  { key: "rounded", name: "Rounded", icon: <Hexagon className="size-3.5" /> },
  { key: "full", name: "Full", icon: <Circle className="size-3.5" /> },
];

const BACKGROUND_OPTIONS = [
  { key: "plain", name: "Plain", icon: <Box className="size-3.5" /> },
  { key: "grid", name: "Grid", icon: <Grid3X3 className="size-3.5" /> },
  { key: "dots", name: "Dots", icon: <CircleDot className="size-3.5" /> },
];

// "purlpe" typo is load-bearing — it's the key stored in users' localStorage.
const PRIMARY_OPTIONS = [
  { key: "orange", name: "Orange", value: "oklch(0.705 0.213 47.604)" },
  { key: "blue", name: "Blue", value: "oklch(0.4151 0.193224 262.085)" },
  { key: "green", name: "Green", value: "oklch(47.70% 0.079 156.98)" },
  { key: "teal", name: "Teal", value: "oklch(0.6049 0.107 184.17)" },
  { key: "purlpe", name: "Purple", value: "oklch(0.606 0.25 292.717)" },
].map((o) => ({
  ...o,
  icon: <CircleDotDashedIcon className="size-3.5 fill-current" style={{ color: o.value }} />,
}));

const PRIMARY_VARS = ["--primary", "--ring", "--sidebar-primary", "--sidebar-ring"];

function applyPrimary(value: string) {
  for (const v of PRIMARY_VARS) {
    document.documentElement.style.setProperty(v, value);
  }
}

/**
 * Re-applies the saved radius preset + primary-color CSS vars. Called once from
 * main.tsx before render — the header toggles whose mount effects used to do
 * this no longer render on dashboard pages. Mode/background need no apply
 * step (ThemeProvider and the background consumers read their own storage).
 */
export function applySavedAppearance() {
  applyRadius(localStorage.getItem("border_radius"));
  const primary = PRIMARY_OPTIONS.find((o) => o.key === localStorage.getItem("primary_color"));
  if (primary) applyPrimary(primary.value);
}

// Explains the row on hover, to the right of the submenu (the offset clears
// its padding + border).
function RowTooltip({ text, children }: { text: string; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={10}>
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

function SettingRow({
  label,
  tooltip,
  value,
  onChange,
  options,
}: {
  label: string;
  tooltip: string;
  value: string;
  onChange: (key: string) => void;
  options: Array<{ key: string; name: string; icon: React.ReactNode }>;
}) {
  return (
    <RowTooltip text={tooltip}>
      <div className="flex items-center justify-between gap-6 px-2 py-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        <Tabs value={value} onValueChange={onChange}>
          <TabsList>
            {options.map((o) => (
              <TabsTrigger
                key={o.key}
                value={o.key}
                aria-label={o.name}
                title={o.name}
                className="px-1.5 py-1"
              >
                {o.icon}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
    </RowTooltip>
  );
}

function SwitchRow({
  id,
  label,
  tooltip,
  value,
  onChange,
}: {
  id: string;
  label: string;
  tooltip: string;
  value: string;
  onChange: (checked: boolean) => void;
}) {
  // The whole row is the switch's <label>, so a click anywhere toggles it.
  return (
    <RowTooltip text={tooltip}>
      <label
        htmlFor={id}
        className="flex cursor-pointer items-center justify-between gap-6 rounded-sm px-2 py-1.5 hover:bg-muted"
      >
        <span className="text-xs text-muted-foreground">{label}</span>
        <Switch id={id} size="xs" checked={value === "enabled"} onCheckedChange={onChange} />
      </label>
    </RowTooltip>
  );
}

export default function AppearanceSettings() {
  const { theme, setTheme } = useTheme();
  const [radius, setRadius] = useState(() => localStorage.getItem("border_radius") ?? "rounded");
  const [background, setBackground] = useState(
    () => localStorage.getItem("background_type") ?? "plain",
  );
  const [primary, setPrimary] = useState(() => localStorage.getItem("primary_color") ?? "orange");
  const [scope, setScope] = useState(() => localStorage.getItem("scope_enabled") ?? "enabled");
  const [easterEgg, setEasterEgg] = useState(
    () => localStorage.getItem("easter_egg_enabled") ?? "enabled",
  );
  const [snowfall, setSnowfall] = useState(
    () => localStorage.getItem("christmas_mode_enabled") ?? "disabled",
  );

  // Listeners (use-scope-enabled, AmyEasterEgg, main.tsx snowfall) read the
  // event's `detail` — they don't re-read localStorage.
  const toggle =
    (storageKey: string, event: string, set: (value: string) => void) => (checked: boolean) => {
      const value = checked ? "enabled" : "disabled";
      localStorage.setItem(storageKey, value);
      set(value);
      window.dispatchEvent(new CustomEvent(event, { detail: value }));
    };

  const handleRadius = (key: string) => {
    localStorage.setItem("border_radius", key);
    setRadius(key);
    applyRadius(key);
  };

  const handleBackground = (key: string) => {
    localStorage.setItem("background_type", key);
    setBackground(key);
    // Background consumers re-read localStorage on this event.
    window.dispatchEvent(new Event("storage"));
  };

  const handlePrimary = (key: string) => {
    const option = PRIMARY_OPTIONS.find((o) => o.key === key);
    if (!option) return;
    localStorage.setItem("primary_color", key);
    setPrimary(key);
    applyPrimary(option.value);
  };

  return (
    <>
      <SettingRow
        label="Theme"
        tooltip="Light, dark, or follow your system setting"
        value={theme}
        onChange={(key) => setTheme(key as "light" | "dark" | "system")}
        options={THEME_OPTIONS}
      />
      <SettingRow
        label="Radius"
        tooltip="How rounded corners are across the app"
        value={radius}
        onChange={handleRadius}
        options={RADIUS_OPTIONS}
      />
      <SettingRow
        label="Background"
        tooltip="Plain, grid or dotted pattern behind the pages"
        value={background}
        onChange={handleBackground}
        options={BACKGROUND_OPTIONS}
      />
      {VITE_ENVIRONMENT != "production" && (
        <SettingRow
          label="Primary Color"
          tooltip="Accent color for buttons, links and highlights"
          value={primary}
          onChange={handlePrimary}
          options={PRIMARY_OPTIONS}
        />
      )}
      <SwitchRow
        id="nav-scope-style"
        label="Scope Style"
        tooltip="Corner brackets around buttons on hover"
        value={scope}
        onChange={toggle("scope_enabled", "scope-toggle", setScope)}
      />
      <DropdownMenuSeparator />
      <SwitchRow
        id="nav-easter-egg"
        label="Amy"
        tooltip='Type "amy" anywhere for a surprise'
        value={easterEgg}
        onChange={toggle("easter_egg_enabled", "easter-egg-toggle", setEasterEgg)}
      />
      <SwitchRow
        id="nav-snowfall"
        label="Snowfall"
        tooltip="Falling snowflakes across the app"
        value={snowfall}
        onChange={toggle("christmas_mode_enabled", "christmas-mode-toggle", setSnowfall)}
      />
    </>
  );
}
